/**
 * VisibilityReconciler — installer for the iOS Safari WS-suspend → resume-
 * reconcile pattern.
 *
 * iOS Safari suspends WebSocket connections when the page goes background; on
 * resume the socket is closed (sometimes silently — `onclose` does not always
 * fire), so the view's WS-pushed state is stale. A pair of listeners catches
 * the resume:
 *
 *   - `visibilitychange` on `document` — fires when the tab goes from hidden
 *     to visible (most resumes).
 *   - `pageshow` on `window` with `event.persisted === true` — fires on
 *     bfcache restore (back/forward navigation that resurrects the page from
 *     the browser's bfcache). `visibilitychange` does NOT fire on bfcache
 *     restore, so this listener is load-bearing — losing it brings the
 *     2026-04-29 «stuck after Back from /map» class of bug back.
 *
 * Both listeners are route-guarded against `location.hash` — only the view
 * that is currently active reconciles. Without this, a backgrounded chat-view
 * + lobby-view + spectator-view would all fire reconcile on the same resume.
 *
 * The reconcile function is the view's existing `gatedReconcileFromServer` /
 * `reconcileChatFromServer` / `reconcileLobbyAgainstServer` / etc. — those
 * functions internally use ReconcileGate, which collapses the
 * visibilitychange + pageshow concurrent firing (iOS Safari sends both events
 * within milliseconds of each other on resume).
 *
 * Sits in 4 views (location/chat/lobby/spectator). Hides the non-obvious
 * `pageshow.persisted` factoid behind a single interface — a future
 * "optimization" that removes the pageshow listener (thinking it duplicates
 * visibilitychange) gets caught by the module's behavioural test, not by a
 * runtime regression weeks later.
 *
 * History: 2026-04-29 retro-audit found that 2 of 4 views had a pageshow
 * handler that ALSO fired on `persisted === false` (regular load), causing a
 * reconcile attempt during initial cold-mount that competed with the cold-
 * mount reconcile itself for the gate's lock. The `event.persisted` guard
 * lives inside this installer now — call sites can't get it wrong.
 */

import type { ViewLifecycle } from './view-lifecycle';

/**
 * Install the visibility/pageshow listener pair on `lc`. Returns nothing —
 * cleanup is automatic via lc.cleanup().
 *
 * @param lc — owner view's lifecycle. Listeners are added via `lc.addListener`
 *             so they tear down with the view.
 * @param reconcileFn — view's reconcile function. Called with no args.
 * @param allowedRouteHash — `location.hash` that the owner view runs on
 *             (e.g. `'#/riddle'`, `'#/chat'`). If the actual hash does not
 *             match at event time, the handler bails — defends against a
 *             stale listener firing for a view that's no longer mounted.
 */
export function installVisibilityReconcile(
  lc: ViewLifecycle,
  reconcileFn: () => void,
  allowedRouteHash: string,
): void {
  const onVisibility = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (window.location.hash !== allowedRouteHash) return;
    reconcileFn();
  };
  const onPageshow = (ev: Event): void => {
    // `persisted` is on PageTransitionEvent specifically. In jsdom + manual
    // dispatchEvent of a plain Event we read `persisted` defensively.
    const pte = ev as PageTransitionEvent;
    if (!pte.persisted) return;
    if (window.location.hash !== allowedRouteHash) return;
    reconcileFn();
  };
  lc.addListener(document, 'visibilitychange', onVisibility);
  lc.addListener(window, 'pageshow', onPageshow);
}
